import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Mail, Phone } from 'lucide-react';
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import './Footer.scss';

import { bankco } from "../../../assets/svg";
import { AdentoWhiteLogo } from "../../../assets/image";

import { FooterLogoData } from "../../../Utils/Data/FooterData";
import { canAccessPublicRewardDetail, canAccessPublicRewardListing, getSubdomainInfo, isPublicPage } from "../../../Utils/CommonFunctions";

import { useAuth } from "../../../hoc/AuthContext";

import FooterLogo from "./FooterLogo";

import { CONTACT_US_PAGE, COOKIE_POLICY_PAGE, POLICY_PAGE, POLICY_PRIVACY_PAGE, TERMS_PAGE } from "../../../Routes/RoutesContant";

import ContactUsModal from "../Modal/ContactUsModal";

import ImageWithFallback from "../ImagewithFallback";

export default function Footer() {
  const queryClient = useQueryClient();
  const headerWebsiteConfig = queryClient.getQueryData(['header-website-config']) || {};
  const configLandingBannerData = queryClient.getQueryData(['integrator-theme', true]) || {};
  const [showContactModal, setShowContactModal] = useState(false);
  const { isAuthenticated } = useAuth();

  const navigate = useNavigate();

  const { isSubdomain, subdomainName } = getSubdomainInfo();
  const subDomain = isSubdomain ? subdomainName : "";
  const rewardSubDomain = ["rewards"];
  const publicLevel = configLandingBannerData?.public_level;
  const normalizedPath = window.location.pathname.replace(/\/+$/, "") || "/";
  const isRewardsRootRoute = normalizedPath === "/rewards";
  const canPublicRewardListing = canAccessPublicRewardListing(publicLevel);
  const canPublicRewardDetail = canAccessPublicRewardDetail(publicLevel);
  const isPublicRewardPreviewRoute = isRewardsRootRoute && canPublicRewardListing && !isAuthenticated;
  const isPublicRewardDetailPreviewRoute = canPublicRewardDetail &&
    /^\/rewards\/(discount|cashback|giftcard|subscription)\/[^/]+\/[^/]+$/.test(normalizedPath);
  const PublicPage = ((isPublicPage(publicLevel) && !isRewardsRootRoute) || isPublicRewardPreviewRoute || isPublicRewardDetailPreviewRoute)
    && !(isPublicRewardDetailPreviewRoute && isAuthenticated);

  // Extract contact information
  const contactInfo = {
    email: configLandingBannerData?.support_email,
    phone: configLandingBannerData?.support_phone_number,
    website: configLandingBannerData?.support_link
  };

  const {
    support_phone_number,
    support_email,
    support_link,
    to_show_contact_us_section
  } = configLandingBannerData || {};

  // Check if ANY contact item should be shown (for container visibility)
  const hasAnyMobileContact = 
    support_phone_number ||
    support_email ||
    support_link ||
    to_show_contact_us_section;

  const hasAnyDesktopContact = 
    support_phone_number ||
    support_email ||
    support_link ||
    to_show_contact_us_section;


  const indiaAddress = "315/274, West End Marg, Saket Near Saket Metro Station New Delhi - 110030";
  const DubaiAddress = "Level I, CP-05, Dubai International Financial Centre, Sheikh Zayed Rd, Dubai, United Arab Emirates";

  function searchGoogle(location) {
    const address = location === "india" ? indiaAddress : DubaiAddress;
    const googleSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(address)}`;
    window.open(googleSearchUrl, '_blank');
  };

  const currentYear = new Date().getFullYear();

  function renderEmailContent() {
    return(
      <div className="contactItem">
        <Mail size={16} />
        <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a>
      </div>
    );
  };

  function renderPhoneNumberContent() {
    return(
      <div className="contactItem">
        <Phone size={16} />
        <a href={`tel:${contactInfo.phone}`}>{contactInfo.phone}</a>
      </div>
    );
  };

  function renderCustomerSupportContent() {
    return(
      <div className="customerSupport">
        <a href={contactInfo.website} target="_blank" rel="noopener noreferrer">
          Customer Support
        </a>
        <ExternalLink size={16} />
      </div>
    );
  };

  function renderContactUsForm() {
    return(
      <div className="contactUsBtn" onClick={() => setShowContactModal(true)}>
        Contact Us
      </div>
    );
  };

  function renderBrandingOnlyFooter( isInfoOnly = false ) {
    const logo = configLandingBannerData?.footer_logo;
    return(
      <div className="footerBrandingOnly"
        style={{ '--footerBrandingOnlyBg': subDomain === "au" && "#6d276c" }}
        //  backgroundColor(configLandingBannerData) }}
      // style={{ backgroundColor: backgroundColor(configLandingBannerData) }}
      >
        {/* Contact Information for mobile view */}
        { hasAnyMobileContact && isInfoOnly && (<div className="footerContactMobile">
          <div className="contactRow">
            {configLandingBannerData?.support_phone_number &&renderPhoneNumberContent()}
            {configLandingBannerData?.support_email && renderEmailContent()}
          </div>
          <div className="contactRow">
            {configLandingBannerData?.support_link && renderCustomerSupportContent()}
            {configLandingBannerData?.to_show_contact_us_section && renderContactUsForm()}
          </div>
        </div>)}

        <div className={`brandingDiv ${isInfoOnly ? "brandingInfoOnlyLayout" : ""}`}>
          {isInfoOnly ? (
            <>
              {logo && (
                <div className="brandingLogoSection">
                  <ImageWithFallback className="brandLogo" alt={"integrator Logo"} src={logo} />
                </div>
              )}

              {hasAnyDesktopContact && (
                <div className="footerContactDesktop">
                  <div className="contactBox">
                    {configLandingBannerData?.support_phone_number && renderPhoneNumberContent()}
                    {configLandingBannerData?.support_email && renderEmailContent()}
                  </div>
                  {configLandingBannerData?.support_link && renderCustomerSupportContent()}
                  {configLandingBannerData?.to_show_contact_us_section && renderContactUsForm()}
                </div>
              )}

              <div className="poweredByContainer">
                powered by 
                <span className="whiteAdentoText">
                  <ImageWithFallback src={AdentoWhiteLogo} alt="Adneto Logo" />
                </span>
              </div>
            </>
          ) : (
            <div className="brandingContent">
              {logo ? (
                <>
                  <ImageWithFallback className="brandLogo" alt={"integrator Logo"} src={logo} />
                  <div className="poweredByContainer">
                    powered by 
                    <span className="whiteAdentoText">
                      <ImageWithFallback src={AdentoWhiteLogo} alt="Adneto Logo" />
                    </span>
                  </div>
                </>
              ) : (
                <div className="poweredBy">
                  powered by
                  <span className="whiteAdentoText">
                    <ImageWithFallback src={AdentoWhiteLogo} alt="Adneto Logo" />
                  </span>
                </div>
              )}
            </div>
          )}

          { isInfoOnly && showContactModal && configLandingBannerData?.to_show_contact_us_section && (
            <ContactUsModal
              isOpen={showContactModal}
              integratorTheme={configLandingBannerData}
              onClose={() => setShowContactModal(false)}
            />
          )}
        </div>
    </div>
    );
  };

  function renderLinkItem (link, text) {
    return <div className="linkItem" onClick={() => navigate(link)}>{text}</div>
  };

  function renderFullFooter() {
    return (
      <div className="footer">
        <div className="mainFooter">
          <div className="footerContent">
            {/* Section 1: Brand */}
            <div className="footerCol brandCol">
              <div className="footerBrandLogo">
                <img src={bankco} alt="Bankco Logo" loading="lazy" />
              </div>
              <p className="description">
                India’s largest AI loyalty network, powering instant rewards for 50+ banks and 2,500+ brands through predictive spending insights.
              </p>
            </div>

            {/* Section 2: Product */}
            <div className="footerCol productCol">
              <h3>Products</h3>
              <div className="linkItem">Discount Offers</div>
              <div className="linkItem">Gift Cards</div>
              <div className="linkItem">Travel</div>
              <div className="linkItem">Precious Metals</div>
            </div>

            {/* Section 3: Legal & Support */}
            <div className="footerCol linksCol">
              <h3>Links</h3>
              {renderLinkItem(`${POLICY_PAGE}/${POLICY_PRIVACY_PAGE}`, "Privacy Policy")}
              {renderLinkItem(`${POLICY_PAGE}/${COOKIE_POLICY_PAGE}`, "Cookie Policy")}
              {renderLinkItem(`${POLICY_PAGE}/${TERMS_PAGE}`, "Terms of Use")}
            </div>

            {/* Section 4: Reach Us */}
            <div className="footerCol contactCol">
              <h3>Contact Us</h3>
              <div className="linkItem" onClick={() => searchGoogle("india")}>
                🇮🇳 <span>New Delhi, India</span><br/>
                {/* {indiaAddress} */}
              </div>
              <div className="linkItem" onClick={() => searchGoogle("dubai")}>
                🇦🇪 <span>Dubai, UAE</span><br/>
                {/* {DubaiAddress} */}
              </div>
              {renderLinkItem(CONTACT_US_PAGE, "Support Desk")}
              {/* <div className="linkItem"><a href="mailto:escalations@adneto.in">✉️ escalations@adneto.in</a></div> */}
            </div>
          </div>
        </div>

        <div className="subFooter">
          <div className="copyright">
            © {currentYear} Adneto Technology Pvt. Ltd. All Rights Reserved.
          </div>
          <div className="socialLinks">
            {FooterLogoData?.map((item, index) => (
              <FooterLogo logo={item.logo} link={item.link} key={index} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (headerWebsiteConfig?.WebsiteConfig?.footer_type === null || headerWebsiteConfig?.WebsiteConfig?.footer_type === undefined) {
    return null;
  }

  const footerbrandingOnly = headerWebsiteConfig?.WebsiteConfig?.footer_type === "branding_only";
  const footerbrandingInfoOnly = headerWebsiteConfig?.WebsiteConfig?.footer_type === "branding_info_only";

  return footerbrandingInfoOnly 
          ? renderBrandingOnlyFooter(true)
          : footerbrandingOnly 
            ? renderBrandingOnlyFooter(false) 
            : rewardSubDomain.includes(subDomain) && PublicPage
              ? renderBrandingOnlyFooter(false) 
              : renderFullFooter();
};
